import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, ReferenceDot,
} from 'recharts';
import {
  niceScale, dateTicks, fmtDay, fmtSpan, fmtInt, tickFormatter, actionStack, WEEK_TICK_STEPS, DAY_TICK_STEPS,
} from '../../lib/slotStatsView';
import {
  sourceColor, fmtRatePct, middleEllipsis, rateN, ofText, ALL_KEY, OTHER_KEY,
} from '../../lib/followBackView';
import {
  AxisFrame, TooltipBox, StackSegment, AXIS_TICK, yAxisWidth, actionFill, onSurface, SERIES_COLOR, Legend,
} from './ChartKit';
import { ActionLegend } from './ActionsCharts';
import { maxDateTicks } from './GrowthCharts';
import { cn } from '@/lib/utils';

/*
 * r16m (2026-10-20): the follow-back charts (design 3.7; dataviz method with the owner's taste - vivid colours, no gap inside a
 * stacked bar, a clickable legend, a tooltip, titled axes with dates, a table twin in the card).
 *
 *   RateTrendChart    follow-back rate by source type over time: one 2px line per type + "All sources" as the neutral context line,
 *                     y = % from 0, the still-maturing tail DASHED over a labelled band, a crosshair tooltip listing every type
 *                     ("6.1% · 23 of 377"), direct labels at the line ends (dropped where they would collide; the legend stays);
 *   EngagedStackChart people engaged by source type on the SAME x axis, under it (small multiples, never a dual axis): the
 *                     owner's seamless StackSegment stack, maturing days in the lighter "so far" fill; its tooltip syncs with the
 *                     trend's crosshair;
 *   TargetRankingChart one row per target, one horizontal bar per source type, sorted by the target's own rate, which sits
 *                     with its name ("15.0% · n 3,899"; the bars carry no numbers - each source's figure is in the tooltip and
 *                     the table), bars under the minimum n faded and such targets last, the scope's average as a reference
 *                     line; a click filters the page to that target (a chip in the filter row); on a phone the name and
 *                     figure sit on a line above the target's bars (never cut at the end);
 *   SlotRateChart     the group's rate by slot in one hue, the batch average as a reference line, the hovered slot lit and the
 *                     others dimmed; a click opens that slot's Stats.
 * Text never wears a data colour; identity rides on the swatch, the line key and the end label.
 */

const SHORT = {
  LIKERS: 'Likers', COMMENTERS: 'Commenters', FOLLOWERS: 'Followers', FOLLOWINGS: 'Followings', POSTERS: 'Posters', DIRECT: 'Direct',
  STORYMENTION: 'Mentions', HIGHLIGHTSHARE: 'Highlights', HE_POSTOWNER: 'Post owners', UNKNOWN: 'Unknown', [OTHER_KEY]: 'Other', [ALL_KEY]: 'All',
};
export const shortLabel = (key, label) => SHORT[key] || label || key;

const pct = (r) => (r === null || r === undefined ? null : r * 100);
const pctTick = (step) => (v) => (v === 0 ? '0%' : `${Number(v.toFixed(step < 1 ? 1 : 0))}%`);

/** The y scale of a rate chart: % from 0, with room above the highest point. */
export function rateScale(maxPct, count = 5) {
  return niceScale(0, Math.max(maxPct, 1), { count, integer: false, zero: true });
}

const bucketTitle = (r) => (r.weekly
  ? `${fmtSpan(r.from, r.to)}${r.days && r.days !== 7 ? ` (${r.days} day${r.days === 1 ? '' : 's'})` : ''}`
  : fmtDay(r.date, 'long'));

/**
 * The trend's tooltip (shared by the line and the bar chart, synced): the bucket, then EVERY shown source type with its rate
 * strong and "23 of 377" after it, "All sources" last; a maturing bucket says so. `mode` 'rate' | 'engaged' picks what leads.
 */
export function TrendTip({ active, payload, keys, hidden = [], labelOf, mode = 'rate', windowDays = 7, show = true, minN = 30 }) {
  if (!active || !show) return null;
  const r = payload && payload[0] ? payload[0].payload : null;
  if (!r) return null;
  const rows = [];
  keys.forEach((k) => {
    const c = r.cells[k];
    const off = hidden.includes(k);
    const lead = mode === 'rate' ? fmtRatePct(c.rate) : fmtInt(c.engaged);
    const few = c.engaged > 0 && !c.plotted ? ` (n ${fmtInt(rateN(c))}: under ${minN}, not drawn)` : '';
    const tail = mode === 'rate'
      ? (c.engaged > 0 ? `${ofText(c)}${few}` : 'nobody engaged')
      : (c.engaged > 0 ? `(${fmtRatePct(c.rate)} back)` : '');
    rows.push({
      key: k, color: sourceColor(k), medium: true, value: lead, muted: off,
      label: mode === 'rate' ? `${labelOf(k)}${off ? ' (hidden)' : ''} · ${tail}` : `${labelOf(k)}${off ? ' (hidden)' : ''} ${tail}`.trim(),
    });
  });
  const a = r.all;
  rows.push({
    key: ALL_KEY, color: sourceColor(ALL_KEY),
    value: mode === 'rate' ? fmtRatePct(a.rate) : fmtInt(a.engaged),
    label: mode === 'rate'
      ? `All sources · ${ofText(a)}`
      : 'people engaged in all',
  });
  const footer = [];
  if (r.maturing) {
    footer.push(<p key="m">Still maturing: some of these people have had less than {windowDays} days to follow back, so the rate can still rise.</p>);
  }
  if (!r.covered && a.engaged > 0) footer.push(<p key="c">No follow-back read covers this {r.weekly ? 'week' : 'day'} yet: the rate is not known.</p>);
  return <TooltipBox title={`${bucketTitle(r)}${r.maturing ? ' · still maturing' : ''}`} rows={rows} footer={footer.length ? footer : null} testid="fb-trend-tooltip" />;
}

/** "still maturing" over the band of the last W days. */
const finite = (v) => typeof v === 'number' && Number.isFinite(v);

function BandLabel({ viewBox, text }) {
  // a data switch can hand one render a viewBox from the old scale (NaN): draw nothing for that render
  if (!viewBox || !finite(viewBox.x) || !finite(viewBox.y)) return null;
  return (
    <text x={viewBox.x + (viewBox.width || 0) / 2} y={viewBox.y + 13} textAnchor="middle" fill="var(--viz-ink-2)" fontSize={11}
      fontWeight={500} data-testid="fb-maturing-band-label">
      {text}
    </text>
  );
}

/** A direct end label: the source's short name beside its line's last point (text ink, never the line colour). */
function EndName({ viewBox, text, series }) {
  if (!viewBox || !finite(viewBox.x) || !finite(viewBox.y)) return null;
  return (
    <text x={viewBox.x + 8} y={viewBox.y + 4} textAnchor="start" fill="var(--viz-ink)" fontSize={11} fontWeight={600}
      className="viz-end-label" stroke="var(--viz-surface)" strokeWidth={3} paintOrder="stroke" strokeLinejoin="round"
      data-testid="fb-end-label" data-series={series}>
      {text}
    </text>
  );
}

/** The rough width of an end label in px (11px semibold): enough to test two labels for overlap. */
const labelWidth = (text) => String(text).length * 6.6 + 10;

/**
 * The end labels that fit (review 2026-10-20): every label sits at the LAST MATURED row - the end of the solid line, where the
 * headline is read - and only a series with a point in that row gets one (a series whose last point is earlier, such as "Other" on a
 * day under n 30, gets none: a label mid-plot would name the wrong line). "All" is placed first and always kept; the others follow
 * from the highest, and one whose box overlaps a placed label's box (x AND y) is dropped - nudging would detach it from its line;
 * the legend and the tooltip still carry it. `xOf(row index)` gives the x in px (null: every label shares one x).
 */
export function endLabels(rows, series, scale, plotHeight, xOf = null) {
  const pxPer = plotHeight / Math.max(1e-9, scale.hi - scale.lo);
  const i = rows.map((r) => !r.maturing).lastIndexOf(true);
  if (i < 0) return [];
  const row = rows[i];
  const x = xOf ? xOf(i) : 0;
  const cands = series.map((s) => {
    const v = row[`r_${s.key}`];
    return v === null || v === undefined ? null : { ...s, date: row.date, v: v * 100, x, y: (scale.hi - v * 100) * pxPer };
  }).filter(Boolean);
  const all = cands.find((c) => c.key === ALL_KEY) || null;
  const rest = cands.filter((c) => c.key !== ALL_KEY).sort((a, b) => b.v - a.v);
  const overlaps = (a, b) => Math.abs(a.y - b.y) < 13 && a.x < b.x + labelWidth(b.label) && b.x < a.x + labelWidth(a.label);
  const placed = all ? [all] : [];
  rest.forEach((c) => { if (!placed.some((p) => overlaps(p, c))) placed.push(c); });
  return placed;
}

/**
 * A point drawn only where the line cannot show it: a value whose neighbours on both sides are missing (a bucket under n between two
 * others) is a dot, so a lone reading is not lost; inside a line no dot is drawn.
 */
export function isolatedDot(rows, field, color, dim) {
  // eslint-disable-next-line react/display-name
  return (p) => {
    const i = p && typeof p.index === 'number' ? p.index : -1;
    const has = (j) => j >= 0 && j < rows.length && rows[j][field] !== null && rows[j][field] !== undefined;
    if (!has(i) || has(i - 1) || has(i + 1) || !finite(p.cx) || !finite(p.cy)) return <g key={`iso-${field}-${i}`} />;
    return <circle key={`iso-${field}-${i}`} cx={p.cx} cy={p.cy} r={3} fill={color} fillOpacity={dim ? 0.3 : 1} stroke="var(--viz-surface)" strokeWidth={1}
      data-testid="fb-iso-dot" />;
  };
}

const TREND_MARGIN = { top: 22, right: 84, bottom: 0, left: 0 };

/**
 * A date tick moved `dx` to the right: the centre of its band, where the point / bar is. Both trend charts force a band scale (so
 * their points and bars share one x), and Recharts then draws the tick labels at the band START.
 */
function BandTick({ x, y, payload, dx = 0 }) {
  if (!finite(x) || !finite(y)) return null;
  return (
    <text x={x + dx} y={y} dy={12} textAnchor="middle" fill={AXIS_TICK.fill} fontSize={AXIS_TICK.fontSize} className="recharts-cartesian-axis-tick-value">
      {fmtDay(payload.value)}
    </text>
  );
}

/**
 * The follow-back rate by source type. trend: trendRows(m); hidden: hidden keys; hi: the highlighted key; labelOf: the server's
 * labels. `yW`: the y axis width shared with the stack below it (so both plots line up).
 */
export function RateTrendChart({
  trend, hidden, hi, onHi, labelOf, yW, scale, height = 250, windowDays, minN, showTip = true, live = true, onOver, testid,
}) {
  const { rows, keys, weekly } = trend;
  const lineKeys = trend.lineKeys || keys;
  const shown = keys.filter((k) => !hidden.includes(k));
  // a source with too few points for a line (trend.sparse) stays in the tooltip, the bars and the table, not as a line
  const lined = shown.filter((k) => lineKeys.includes(k));
  const days = rows.map((r) => r.date);
  const mat = rows.filter((r) => r.maturing);
  const fmt = pctTick(scale.step);
  const series = [...lined.map((k) => ({ key: k, label: shortLabel(k, labelOf(k)) })), { key: ALL_KEY, label: 'All' }];
  const plotH = height - TREND_MARGIN.top - TREND_MARGIN.bottom - 30;
  const endLabelsFor = (width) => {
    if (!(lined.length + 1 <= 5 && width >= 480)) return [];
    const band = (width - yW - TREND_MARGIN.left - TREND_MARGIN.right) / Math.max(1, rows.length);
    return endLabels(rows, series, { lo: scale.lo, hi: scale.hi }, plotH, (i) => yW + TREND_MARGIN.left + (i + 0.5) * band);
  };
  const yTitle = 'Follow-back rate';
  const everyDot = trend.weekly && rows.length <= 16;
  const lineProps = (k, dashed) => {
    const lit = hi === k;
    const dim = !!hi && !lit && k !== ALL_KEY;
    return {
      type: 'linear',
      dataKey: (r) => { const v = r[`${dashed ? 'm' : 'r'}_${k}`]; return v === null || v === undefined ? null : v * 100; },
      name: `${labelOf(k)}${dashed ? ' (still maturing)' : ''}`,
      stroke: sourceColor(k),
      strokeWidth: lit ? 2.75 : 2,
      strokeOpacity: dim ? 0.3 : 1,
      strokeLinejoin: 'round',
      strokeLinecap: 'round',
      strokeDasharray: dashed ? '5 4' : undefined,
      dot: everyDot ? { r: 3, strokeWidth: 0, fill: sourceColor(k), fillOpacity: dim ? 0.3 : 1 } : isolatedDot(rows, `${dashed ? 'm' : 'r'}_${k}`, sourceColor(k), dim),
      // no crosshair marks once the pointer and the focus have left (Recharts keeps a keyboard position active)
      activeDot: !live ? false : (dashed || k === ALL_KEY
        ? { r: 4, stroke: 'var(--viz-surface)', strokeWidth: 2, fill: sourceColor(k) }
        : { r: 5, stroke: 'var(--viz-surface)', strokeWidth: 2, fill: sourceColor(k) }),
      connectNulls: false,
      isAnimationActive: false,
    };
  };
  return (
    <div onFocus={() => onOver && onOver('rate')} onBlur={() => onOver && onOver('blur')}>
    <AxisFrame yTitle={yTitle} xTitle={weekly ? 'Week engaged' : 'Day engaged'} testid={testid}>
      {(width) => (
        <LineChart width={width} height={height} data={rows} margin={TREND_MARGIN} syncId="fb-trend" role="img"
          onMouseEnter={() => onOver && onOver('rate')}
          onMouseLeave={() => { if (onHi) onHi(null); if (onOver) onOver(null); }}
          aria-label={`${yTitle} by source type (${lined.map(labelOf).join(', ')} and all sources), ${fmtDay(days[0], 'medium')} to `
            + `${fmtDay(days[days.length - 1], 'medium')}${mat.length ? `; from ${fmtDay(mat[0].date, 'medium')} still maturing (dashed)` : ''}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          {mat.length > 0 && (
            <ReferenceArea x1={mat[0].date} x2={mat[mat.length - 1].date} fill="var(--viz-hover)" fillOpacity={1} stroke="none"
              ifOverflow="hidden" label={<BandLabel text="still maturing" />} />
          )}
          <XAxis dataKey="date" scale="band" ticks={dateTicks(days, maxDateTicks(width), weekly ? WEEK_TICK_STEPS : DAY_TICK_STEPS)} interval={0}
            tickFormatter={(d) => fmtDay(d)} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8}
            tick={(p) => <BandTick {...p} dx={(width - yW - TREND_MARGIN.left - TREND_MARGIN.right) / Math.max(1, rows.length) / 2} />} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt} width={yW} tick={AXIS_TICK}
            tickLine={false} axisLine={false} tickMargin={6} allowDataOverflow />
          <Tooltip isAnimationActive={false} cursor={live ? { stroke: 'var(--viz-ink-3)', strokeWidth: 1 } : false}
            content={(p) => <TrendTip {...p} keys={keys} hidden={hidden} labelOf={labelOf} windowDays={windowDays} show={showTip} minN={minN} />} />
          {/* the context line first, so the source lines draw over it */}
          <Line key={`r_${ALL_KEY}`} {...lineProps(ALL_KEY, false)} />
          <Line key={`m_${ALL_KEY}`} {...lineProps(ALL_KEY, true)} />
          {lined.map((k) => <Line key={`r_${k}`} {...lineProps(k, false)} />)}
          {lined.map((k) => <Line key={`m_${k}`} {...lineProps(k, true)} />)}
          {endLabelsFor(width).map((l) => (
            <ReferenceDot key={`end-${l.key}`} x={l.date} y={l.v} r={0} fill="none" stroke="none" ifOverflow="visible"
              label={<EndName text={l.label} series={l.key} />} />
          ))}
        </LineChart>
      )}
    </AxisFrame>
    </div>
  );
}

/** People engaged by source type, stacked (StackSegment with the source keys), on the trend's x axis. */
export function EngagedStackChart({
  trend, hidden, hi, onHi, labelOf, yW, scale, height = 170, windowDays, minN, showTip = true, live = true, onOver, testid,
}) {
  const { rows, keys, weekly } = trend;
  const stack = actionStack(rows, keys, hidden, { weekly: true });
  const days = rows.map((r) => r.date);
  const fmt = tickFormatter(scale.step, scale.hi);
  const yTitle = `People engaged per ${weekly ? 'week' : 'day'}`;
  return (
    <div onFocus={() => onOver && onOver('stack')} onBlur={() => onOver && onOver('blur')}>
    <AxisFrame yTitle={weekly ? 'Engaged / week' : 'Engaged / day'} xTitle={weekly ? 'Week engaged' : 'Day engaged'} testid={testid}>
      {(width) => (
        <BarChart key={keys.join(',')} width={width} height={height} data={stack.rows} margin={{ ...TREND_MARGIN, top: 10 }} syncId="fb-trend"
          barCategoryGap={weekly ? '28%' : '18%'} role="img" onMouseEnter={() => onOver && onOver('stack')}
          onMouseLeave={() => { if (onHi) onHi(null); if (onOver) onOver(null); }}
          aria-label={`${yTitle} by source type, ${fmtDay(days[0], 'medium')} to ${fmtDay(days[days.length - 1], 'medium')}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis dataKey="date" scale="band" ticks={dateTicks(days, maxDateTicks(width), weekly ? WEEK_TICK_STEPS : DAY_TICK_STEPS)} interval={0}
            tickFormatter={(d) => fmtDay(d)} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8}
            tick={(p) => <BandTick {...p} dx={(width - yW - TREND_MARGIN.left - TREND_MARGIN.right) / Math.max(1, rows.length) / 2} />} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt} width={yW} tick={AXIS_TICK}
            tickLine={false} axisLine={false} tickMargin={6} allowDataOverflow />
          <Tooltip isAnimationActive={false} cursor={live ? { fill: 'var(--viz-hover)' } : false}
            content={(p) => <TrendTip {...p} keys={keys} hidden={hidden} labelOf={labelOf} mode="engaged" windowDays={windowDays} show={showTip} minN={minN} />} />
          {/* every key keeps its <Bar> (a hidden one draws nothing), so the stack order never changes (see ActionsCharts) */}
          {keys.map((k) => (
            <Bar key={k} dataKey={(r) => r.seg[k]} name={labelOf(k)} stackId="engaged" fill={sourceColor(k)} maxBarSize={24} isAnimationActive={false}
              shape={(p) => <StackSegment {...p} keys={keys} typeKey={k} color={sourceColor(k)} hi={hi} onHi={onHi} />}
              activeBar={(p) => <StackSegment {...p} keys={keys} typeKey={k} color={sourceColor(k)} hi={hi} onHi={onHi} isActive={live} />} />
          ))}
        </BarChart>
      )}
    </AxisFrame>
    </div>
  );
}

/** The y axis width both trend plots use (the wider of the two), so their x axes line up. */
export function sharedAxisWidth(rateSc, engagedSc) {
  return Math.max(yAxisWidth(rateSc.ticks, pctTick(rateSc.step)), yAxisWidth(engagedSc.ticks, tickFormatter(engagedSc.step, engagedSc.hi)));
}

/**
 * The source-type legend: a click hides / shows a type in both trend charts (the last one stays), hover or focus highlights it; the
 * "All sources" context line is listed after them (always drawn).
 */
export function SourceLegend({ keys, hidden, hi, onToggle, onHi, onShowAll, labelOf, testid = 'fb-legend' }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <ActionLegend series={keys.map((k) => ({ key: k, label: labelOf(k) }))} hidden={hidden} hi={hi} onToggle={onToggle} onHi={onHi}
        onShowAll={onShowAll} testid={testid} colorOf={sourceColor} noun="source types" label="Source types: click to hide or show" />
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`${testid}-all`}>
        <span className="inline-block h-0.5 w-3.5 rounded-full" style={{ background: sourceColor(ALL_KEY) }} aria-hidden />
        All sources
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`${testid}-maturing`}>
        <span className="inline-block w-3.5 border-t-2 border-dashed" style={{ borderColor: 'var(--viz-ink-3)' }} aria-hidden />
        Still maturing
      </span>
    </div>
  );
}

/** Hover state with the 80 ms leave debounce of the Actions stack (moving between marks does not flicker). */
export function useHighlight() {
  const [hi, setHi] = useState(null);
  const timer = useRef(null);
  const onHi = useCallback((k) => {
    clearTimeout(timer.current);
    if (k) setHi(k);
    else timer.current = setTimeout(() => setHi(null), 80);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [hi, onHi];
}

/* ------------------------------------------------------------------ by target */

/** A horizontal bar with a 4px rounded data end on the right and a square base on the left. */
export function hBarPath(x, y, w, h, r = 4) {
  if (!(w > 0) || !(h > 0)) return '';
  const rr = Math.max(0, Math.min(r, h / 2, w));
  const R = x + w;
  return `M${x},${y}H${R - rr}Q${R},${y} ${R},${y + rr}V${y + h - rr}Q${R},${y + h} ${R - rr},${y + h}H${x}Z`;
}

function HBar(props) {
  const { x, y, width, height, payload, srcKey, isActive } = props;
  if (!(width > 0)) return null;
  const part = payload && payload.parts && payload.parts[srcKey];
  const rankable = !!(payload && payload.rankable && part && part.rankable);
  const base = sourceColor(srcKey);
  return (
    <path d={hBarPath(x, y, width, height)} fill={rankable ? actionFill(base, { active: !!isActive }) : onSurface(base, isActive ? 0.45 : 0.32)}
      className="viz-bar" data-src={srcKey} data-rankable={rankable ? 'yes' : 'no'} />
  );
}

/** A target's own figure, the one it is sorted by: "15.0% · n 3,899" (all its sources; "too few" under the minimum n). */
export function targetFigure(row) {
  const r = row.rate === null ? '-' : fmtRatePct(row.rate);
  return `${r} · n ${fmtInt(rateN(row))}${row.rankable ? '' : ' · too few'}`;
}

/** The px the 11px label font takes per character (a little generous: the names are mostly lower case and digits). */
const CH_PX = 6.3;

/**
 * The target's label. Desktop: the name (middle-shortened: the start and the end of a handle tell one brand's accounts apart) right
 * of the axis, and under it the target's own rate and n - the figure it is sorted by and the "Best target" tile reads. Phone
 * (`header`): ONE line above the target's bars, from the plot's left edge - the full name, then the figure; a name too long for the
 * line is shortened in the middle, never at the end.
 */
function TargetTick({ x, y, payload, rows, maxChars, onPick, header, headerX, headerW, barsH }) {
  const row = rows.find((r) => r.target === payload.value);
  if (!row || !finite(x) || !finite(y)) return null;
  const full = String(payload.value);
  const figure = targetFigure(row);
  const ink = row.rankable ? 'var(--viz-ink)' : 'var(--viz-ink-3)';
  const nameInk = row.rankable ? 'var(--viz-ink-2)' : 'var(--viz-ink-3)';
  const common = {
    style: { cursor: onPick ? 'pointer' : 'default' },
    // the chart's own onClick would pick its last HOVERED row (a stale one when the pointer comes from the bars): the name wins
    onClick: (e) => { if (e && e.stopPropagation) e.stopPropagation(); if (onPick) onPick(payload.value); },
    'data-testid': 'fb-target-row-label',
    'data-target': full,
  };
  if (header) {
    const room = Math.max(8, Math.floor((headerW - (figure.length + 3) * CH_PX) / CH_PX));
    return (
      <g {...common}>
        <title>{`${full}: ${figure}`}</title>
        <text x={headerX} y={y - barsH / 2 - 6} textAnchor="start" fontSize={11} stroke="var(--viz-surface)" strokeWidth={4}
          paintOrder="stroke" strokeLinejoin="round">
          <tspan fill={nameInk} fontWeight={500}>{middleEllipsis(full, room)}</tspan>
          <tspan fill={ink} fontWeight={600} dx={6}>{figure}</tspan>
        </text>
      </g>
    );
  }
  return (
    <g {...common} transform={`translate(${x},${y})`}>
      <title>{`${full}: ${figure}`}</title>
      <text x={-8} y={-2} textAnchor="end" fontSize={11} fill={nameInk} fontWeight={500}>{middleEllipsis(full, maxChars)}</text>
      <text x={-8} y={12} textAnchor="end" fontSize={11} fill={ink} fontWeight={600} className="tnum">{figure}</text>
    </g>
  );
}

function TargetTip({ active, payload, keys, labelOf, minRankN }) {
  if (!active) return null;
  const r = payload && payload[0] ? payload[0].payload : null;
  if (!r) return null;
  const rows = [{
    key: 'all', value: fmtRatePct(r.rate),
    label: `all sources · ${ofText(r)}`,
  }];
  keys.forEach((k) => {
    const c = r.parts[k];
    if (!c || !(c.engaged > 0)) return;
    const few = c.rankable ? '' : ` (n ${fmtInt(rateN(c))}: too few)`;
    rows.push({
      key: k, color: sourceColor(k), medium: true, value: fmtRatePct(c.rate),
      label: `${labelOf(k)} · ${ofText(c)}${few}`,
    });
  });
  const footer = [];
  if (!r.rankable) footer.push(<p key="n">n {fmtInt(rateN(r))}: too few to rank (fewer than {minRankN} people).</p>);
  footer.push(<p key="l">Click to show only this target.</p>);
  return <TooltipBox title={r.target} rows={rows} footer={footer} testid="fb-target-tooltip" />;
}

/**
 * The target ranking. ranking: targetRanking(m). onPick(target) sets the target chip. Height grows with the rows. Each target's own
 * rate and n sit with its name (the sort key); the bars carry no numbers (a number on every bar is noise - each source's figure is in
 * the tooltip and the table twin). `compact` (a phone): the name and figure on a line above the target's bars, the bars full width.
 */
export function TargetRankingChart({ ranking, labelOf, minRankN, onPick, compact = false, testid }) {
  const { rows, keys, average } = ranking;
  const maxRate = Math.max(1, ...rows.flatMap((r) => keys.map((k) => pct(r.parts[k] && r.parts[k].rate) || 0)), pct(average) || 0);
  const scale = niceScale(0, maxRate, { count: compact ? 4 : 5, integer: false, zero: true });
  const fmt = pctTick(scale.step);
  const barH = keys.length > 3 ? 9 : 10;
  const barsH = keys.length * barH + (keys.length - 1) * 3;
  // a phone needs room above each group for its header line; a desktop row holds the two label lines beside the bars
  const rowH = compact ? barsH + 34 : Math.max(barsH + 16, 40);
  // a phone keeps 10px left of the plot, so the "0%" tick is not cut
  const margin = { top: 18, right: compact ? 12 : 24, bottom: 0, left: compact ? 10 : 0 };
  const height = rows.length * rowH + margin.top + 30;
  const data = rows.map((r) => {
    const d = { ...r };
    keys.forEach((k) => { d[`v_${k}`] = r.parts[k] && r.parts[k].rate !== null && r.parts[k].engaged > 0 ? r.parts[k].rate * 100 : null; });
    return d;
  });
  const maxChars = 22;
  const longestFigure = Math.max(...rows.map((r) => targetFigure(r).length));
  const yW = compact ? 2 : Math.min(176, Math.max(84, Math.max(...rows.map((r) => Math.min(maxChars, r.target.length)), longestFigure) * CH_PX + 14));
  return (
    <AxisFrame yTitle="Target account" xTitle="Follow-back rate" testid={testid}>
      {(width) => (
        <BarChart layout="vertical" width={width} height={height} data={data} margin={margin} barGap={3} role="img"
          onClick={(st) => { if (onPick && st && st.activeLabel !== undefined && st.activeLabel !== null) onPick(String(st.activeLabel)); }}
          aria-label={`Follow-back rate by target account and source type, ${rows.length} targets, sorted by each target's rate; average ${fmtRatePct(average)}`}>
          <CartesianGrid horizontal={false} stroke="var(--viz-grid)" />
          <XAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt} tick={AXIS_TICK} tickLine={false}
            axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={6} allowDataOverflow />
          <YAxis type="category" dataKey="target" width={yW} interval={0} tickLine={false} axisLine={false}
            tick={(p) => <TargetTick {...p} rows={rows} maxChars={maxChars} onPick={onPick} header={compact} headerX={margin.left + yW}
              headerW={width - margin.left - yW - margin.right} barsH={barsH} />} />
          <Tooltip isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }}
            content={(p) => <TargetTip {...p} keys={keys} labelOf={labelOf} minRankN={minRankN} />} />
          {keys.map((k) => (
            <Bar key={k} dataKey={`v_${k}`} name={labelOf(k)} barSize={barH} isAnimationActive={false} style={{ cursor: onPick ? 'pointer' : 'default' }}
              shape={(p) => <HBar {...p} srcKey={k} />} activeBar={(p) => <HBar {...p} srcKey={k} isActive />} />
          ))}
          {average !== null && (
            <ReferenceLine x={average * 100} stroke="var(--viz-ink-2)" strokeDasharray="4 3" strokeWidth={1} ifOverflow="extendDomain"
              label={{ value: `average ${fmtRatePct(average)}`, position: 'top', fill: 'var(--viz-ink-2)', fontSize: 11 }} />
          )}
        </BarChart>
      )}
    </AxisFrame>
  );
}

/* ------------------------------------------------------------------ by slot */

function SlotBar(props) {
  const { x, y, width, height, payload, hi, onHi } = props;
  if (!(height > 0) || !(width > 0)) return null;
  const lit = hi === payload.slot;
  const dim = !!hi && !lit;
  const partial = !payload.signalOk;
  return (
    <path d={`M${x},${y + height}V${y + Math.min(4, height)}Q${x},${y} ${x + Math.min(4, width / 2)},${y}H${x + width - Math.min(4, width / 2)}Q${x + width},${y} ${x + width},${y + Math.min(4, height)}V${y + height}Z`}
      fill={actionFill(SERIES_COLOR, { partial, lit, dim })} className={cn('viz-bar viz-seg', lit && 'viz-seg-lit')}
      data-slot={payload.slot} onMouseEnter={() => onHi && onHi(payload.slot)} onMouseLeave={() => onHi && onHi(null)} />
  );
}

function SlotTip({ active, payload, minRankN }) {
  if (!active) return null;
  const r = payload && payload[0] ? payload[0].payload : null;
  if (!r) return null;
  const rows = [{ key: 'r', color: SERIES_COLOR, value: fmtRatePct(r.rate), label: r.engaged > 0 ? `${ofText(r)}${r.rate === null ? '' : ' followed back'}` : 'nobody engaged' }];
  if (r.device) rows.push({ key: 'd', value: r.device, label: 'phone', medium: true });
  const footer = [];
  if (r.stuck) {
    footer.push(<p key="b">{r.stuck.status === 'DETACHED' ? 'Detached from the batch' : 'Not a member of the batch'}: in batch mode it engages nobody until it is joined.</p>);
  }
  if (!r.signalOk) footer.push(<p key="s">Its follow-back list was not read in the last 24 h.</p>);
  if (!r.rankable) footer.push(<p key="n">n {fmtInt(rateN(r))}: too few to rank (fewer than {minRankN}).</p>);
  footer.push(<p key="o">Click to open this slot&apos;s Stats.</p>);
  return <TooltipBox title={r.slot} rows={rows} footer={footer} testid="fb-slot-tooltip" />;
}

/** The group's rate by slot (one hue), with the batch average. rows: slotRows(m) in slot order. */
export function SlotRateChart({ rows, average, minRankN, onOpen, height = 220, testid }) {
  const anyQuiet = rows.some((r) => !r.signalOk);
  const [hi, onHi] = useHighlight();
  const data = rows.map((r) => ({ ...r, v: r.rate === null ? null : r.rate * 100, short: r.number !== null ? String(r.number) : r.slot }));
  const maxPct = Math.max(1, ...data.map((d) => d.v || 0), pct(average) || 0);
  const scale = rateScale(maxPct, 5);
  const fmt = pctTick(scale.step);
  const ticks = data.map((d) => d.short);
  return (
    <div>
    <AxisFrame yTitle="Follow-back rate" xTitle="Slot" testid={testid}>
      {(width) => {
        const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(width / 44))));
        return (
          <BarChart width={width} height={height} data={data} margin={{ top: 20, right: 64, bottom: 0, left: 0 }} barCategoryGap="22%" role="img"
            onClick={(st) => {
              const i = st && typeof st.activeIndex === 'number' ? st.activeIndex : Number(st && st.activeIndex);
              if (onOpen && Number.isInteger(i) && data[i]) onOpen(data[i].slot);
            }}
            onMouseLeave={() => onHi(null)}
            aria-label={`Follow-back rate by slot, ${data.length} slots; batch average ${fmtRatePct(average)}`}>
            <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
            <XAxis dataKey="short" ticks={ticks.filter((_, i) => i % every === 0)} interval={0} tick={AXIS_TICK} tickLine={false}
              axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8} />
            <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt} width={yAxisWidth(scale.ticks, fmt)}
              tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6} allowDataOverflow />
            <Tooltip isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }} content={(p) => <SlotTip {...p} minRankN={minRankN} />} />
            <Bar dataKey="v" name="Follow-back rate" maxBarSize={24} isAnimationActive={false} style={{ cursor: 'pointer' }}
              shape={(p) => <SlotBar {...p} hi={hi} onHi={onHi} />} activeBar={(p) => <SlotBar {...p} hi={hi} onHi={onHi} />} />
            {average !== null && (
              <ReferenceLine y={average * 100} stroke="var(--viz-ink-2)" strokeDasharray="4 3" strokeWidth={1}
                label={{ value: `avg ${fmtRatePct(average)}`, position: 'right', fill: 'var(--viz-ink-2)', fontSize: 11 }} />
            )}
          </BarChart>
        );
      }}
    </AxisFrame>
    <div className="mt-2 pl-5">
      <Legend testid={testid && `${testid}-legend`} items={[
        { key: 'rate', label: 'Slot follow-back rate', color: SERIES_COLOR },
        ...(anyQuiet ? [{ key: 'quiet', label: 'Lighter: its list was not read in the last 24 h', color: actionFill(SERIES_COLOR, { partial: true }) }] : []),
        { key: 'avg', label: 'Group average', color: 'var(--viz-ink-2)', kind: 'dash' },
      ]} />
    </div>
    </div>
  );
}
